// src/pages/tag/TagDetailPage.tsx
// Auto-generated by corebuilder. Edit freely.

import { useEffect } from 'react';
import { Link, useParams, useNavigate } from 'react-router-dom';
import { observer } from '@react';
import { useTag, useI18n } from '@react/hooks';
import {
  Button,
  Card,
  CardHeader,
  CardTitle,
  CardDescription,
  CardContent,
  Alert,
  ErrorAlert,
} from '@react/components';

/**
 * Tag detail page.
 */
export const TagDetailPage = observer(() => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const store = useTag();
  const { t } = useI18n();
  const item = store.currentItem;

  useEffect(() => {
    if (id) {
      store.GetTag(id);
    }
    return () => {
      store.clearCurrent();
    };
  }, [id]);
  const handleDelete = async () => {
    if (!id || !confirm(t('tag.deleteConfirm'))) return;
    try {
      await store.DeleteTag(id);
      navigate('/tags');
    } catch {
      // Error is handled by store
    }
  };

  if (store.isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-muted-foreground">{t('common.loading')}</div>
      </div>
    );
  }

  if (store.error) {
    return (
      <>
        <ErrorAlert error={store.error} />
        <Link to="/tags" className="mt-2 inline-block text-sm underline text-primary">
          {t('common.backToList')}
        </Link>
      </>
    );
  }
  if (!item) {
    return (
      <Alert variant="warning" title={t('common.notFound')}>
        <Link to="/tags" className="mt-2 inline-block text-sm underline text-primary">
          {t('common.backToList')}
        </Link>
      </Alert>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <nav className="text-sm mb-2">
            <Link to="/tags" className="text-muted-foreground hover:text-foreground">
              {t('tag.list')}
            </Link>
            <span className="mx-2 text-muted-foreground">/</span>
            <span className="text-foreground">{t('tag.details')}</span>
          </nav>
          <h1 className="text-2xl font-bold text-foreground">{t('tag.details')}</h1>
        </div>
        <div className="flex gap-2">
          <Link to={`/tags/${id}/edit`}>
            <Button variant="secondary">{t('common.edit')}</Button>
          </Link>
          <Button variant="danger" onClick={handleDelete}>
            {t('common.delete')}
          </Button>
        </div>
      </div>
      <Card>
        <CardHeader>
          <CardTitle>{t('tag.details')}</CardTitle>
          <CardDescription>{t('tag.title')}</CardDescription>
        </CardHeader>
        <CardContent>
          <dl className="divide-y divide-border">
            <div className="py-3 grid grid-cols-3 gap-4">
              <dt className="text-sm font-medium text-muted-foreground">Entity</dt>
              <dd className="text-sm text-foreground col-span-2">
                {String((item as any)?.entity ?? '')}
              </dd>
            </div>
            <div className="py-3 grid grid-cols-3 gap-4">
              <dt className="text-sm font-medium text-muted-foreground">Name</dt>
              <dd className="text-sm text-foreground col-span-2">
                {String((item as any)?.name ?? '')}
              </dd>
            </div>
            <div className="py-3 grid grid-cols-3 gap-4">
              <dt className="text-sm font-medium text-muted-foreground">Description</dt>
              <dd className="text-sm text-foreground col-span-2">
                {String((item as any)?.description ?? '')}
              </dd>
            </div>
            <div className="py-3 grid grid-cols-3 gap-4">
              <dt className="text-sm font-medium text-muted-foreground">Color</dt>
              <dd className="text-sm text-foreground col-span-2">
                {String((item as any)?.color ?? '')}
              </dd>
            </div>
            <div className="py-3 grid grid-cols-3 gap-4">
              <dt className="text-sm font-medium text-muted-foreground">Icon</dt>
              <dd className="text-sm text-foreground col-span-2">
                {String((item as any)?.icon ?? '')}
              </dd>
            </div>
            <div className="py-3 grid grid-cols-3 gap-4">
              <dt className="text-sm font-medium text-muted-foreground">Scope Product Id</dt>
              <dd className="text-sm text-foreground col-span-2">
                {String((item as any)?.scope_product_id ?? '')}
              </dd>
            </div>
            <div className="py-3 grid grid-cols-3 gap-4">
              <dt className="text-sm font-medium text-muted-foreground">Created By</dt>
              <dd className="text-sm text-foreground col-span-2">
                {String((item as any)?.created_by ?? '')}
              </dd>
            </div>
            <div className="py-3 grid grid-cols-3 gap-4">
              <dt className="text-sm font-medium text-muted-foreground">Usage Count</dt>
              <dd className="text-sm text-foreground col-span-2">
                {String((item as any)?.usage_count ?? '')}
              </dd>
            </div>
            <div className="py-3 grid grid-cols-3 gap-4">
              <dt className="text-sm font-medium text-muted-foreground">Aspects</dt>
              <dd className="text-sm text-foreground col-span-2">
                {String((item as any)?.aspects ?? '')}
              </dd>
            </div>
            <div className="py-3 grid grid-cols-3 gap-4">
              <dt className="text-sm font-medium text-muted-foreground">Metadata</dt>
              <dd className="text-sm text-foreground col-span-2">
                {String((item as any)?.metadata ?? '')}
              </dd>
            </div>
            <div className="py-3 grid grid-cols-3 gap-4">
              <dt className="text-sm font-medium text-muted-foreground">Created At</dt>
              <dd className="text-sm text-foreground col-span-2">
                {String((item as any)?.created_at ?? '')}
              </dd>
            </div>
            <div className="py-3 grid grid-cols-3 gap-4">
              <dt className="text-sm font-medium text-muted-foreground">Updated At</dt>
              <dd className="text-sm text-foreground col-span-2">
                {String((item as any)?.updated_at ?? '')}
              </dd>
            </div>
          </dl>
        </CardContent>
      </Card>
    </div>
  );
});

export default TagDetailPage;
